/**
 * App — routing shell and the single mount point for the shared providers.
 *
 * Scroll architecture (Task 1)
 * ---------------------------
 * v2 wrapped the sections in
 *   <main style="height:100vh" overflow-y-auto snap-y snap-mandatory>
 * with fixed spacer divs between them. That inner div was the only scroller,
 * which is what produced the lock/erratic overflow behaviour: mandatory snap
 * fought the fixed spacers, `100vh` ignored mobile browser chrome, and touch
 * inertia was lost because the scroller was a div rather than the document.
 *
 * v3 lets the DOCUMENT scroll (see base.css). Sections size themselves with
 * `min-height: var(--viewport-h)` and use `scroll-snap-type: y proximity`, so
 * snapping is a suggestion and never a cage. The spacer divs are gone — the
 * rhythm now comes from section padding, which means nothing can be clipped.
 *
 * Providers are mounted here so state survives navigation between / and /about:
 * the stream keeps playing and the Telegram session/inbox stay alive.
 */

import React, { useCallback, useState } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import Atmosphere from './components/Atmosphere';
import RadioPlayer from './components/RadioPlayer';
import TerminalSection from './components/TerminalSection';
import TelegramEmbed from './components/TelegramEmbed';
import UplinkBar from './components/UplinkBar';
import MoonyModal from './components/MoonyModal';
import About from './pages/About';
import { RadioProvider } from './lib/RadioContext';
import { CommsProvider, useComms } from './lib/CommsContext';
import { SITE } from './lib/config';

function Home({ onOpenMoony, unreadReplies }) {
  return (
    <>
      {/* The document scrolls; these sections are plain flow children. */}
      <RadioPlayer />
      <TerminalSection onOpenMoony={onOpenMoony} />
      <TelegramEmbed />
      <UplinkBar onOpenMoony={onOpenMoony} unreadReplies={unreadReplies} />
    </>
  );
}

function AboutRoute({ onOpenMoony, unreadReplies }) {
  return (
    <>
      <About />
      {/* The bar stays reachable on the About page too. */}
      <UplinkBar onOpenMoony={onOpenMoony} unreadReplies={unreadReplies} />
    </>
  );
}

/** Reads relay state for the badge; kept inside the provider tree. */
function Shell() {
  const { unreadReplies } = useComms();
  const [moonyOpen, setMoonyOpen] = useState(false);

  const openMoony = useCallback(() => setMoonyOpen(true), []);
  const closeMoony = useCallback(() => setMoonyOpen(false), []);

  return (
    <>
      <Routes>
        <Route path="/" element={<Home onOpenMoony={openMoony} unreadReplies={unreadReplies} />} />
        <Route
          path="/about"
          element={<AboutRoute onOpenMoony={openMoony} unreadReplies={unreadReplies} />}
        />
      </Routes>

      <Footer />
      <Atmosphere />
      <MoonyModal open={moonyOpen} onClose={closeMoony} />
    </>
  );
}

function Footer() {
  return (
    <footer
      className="relative z-10 border-t border-[color:var(--edge)] bg-[#050505] px-4 py-6 text-center"
      style={{ paddingBottom: 'calc(1.5rem + 5.5rem)' }}
    >
      <p className="dmr-label" data-testid="site-version">
        {SITE.version}
      </p>
      <p className="dmr-hint mt-1" data-testid="site-tagline">
        {SITE.tagline}
      </p>
    </footer>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <RadioProvider>
        <CommsProvider>
          <Shell />
        </CommsProvider>
      </RadioProvider>
    </BrowserRouter>
  );
}
